import React, {
	useState,
	useEffect,
	useRef,
	useMemo,
	useCallback,
} from 'react';
import { useForm, useFieldArray } from 'react-hook-form';
import { z } from 'zod';
import NewClassificacaoPDF from '../classificacao/newClassificacaoPDF';
import NewNotificacaoPDF from '../notificacao/newNotificacaoPDF.js';
import { zodResolver } from '@hookform/resolvers/zod';

import { generatePDF } from '../pdfGenerator.js';
import { FaRegFilePdf } from 'react-icons/fa';

import { GoPlus } from 'react-icons/go';
import { LuMinus } from 'react-icons/lu';
import Cookies from 'js-cookie';

import {
	requestInvByProtocol,
	requestNewInvCreation,
	requestSaveForm,
	requestInvConclude,
	requestSavedData,
} from '../../../services/requests';
import { getLatestSavedRecord } from './investigacaoUtils';

import NewNotificacaoPreenchido from '../notificacao/newNotificacaoPreenchido.js';
import NewClassificacaoPreenchido from '../classificacao/newClassificacaoPreenchido.js';
import FinishCard from '../finishCard';
import logo from '../../../images/bluelogo.png';

// classes base para inputs/textarea
const inputBaseClass =
	'w-full min-h-[40px] border border-[#E3E3E3] rounded px-2 py-1 text-sm focus:outline-none focus:ring-1 focus:ring-[#004A80] placeholder:text-gray-400';
const textareaBaseClass =
	'w-full h-[40px] min-h-[40px] border border-[#E3E3E3] rounded px-2 py-1 text-sm focus:outline-none focus:ring-1 focus:ring-[#004A80] placeholder:text-gray-400';

// --- Schema Zod (validação forte) -------------------------------------------

const equipeSchema = z.object({
	nome_completo: z.string().min(1, 'Informe o nome completo'),
	cargo: z.string().min(1, 'Informe o cargo'),
	setor: z.string().min(1, 'Informe o setor'),
	email: z.string().email('Email inválido'),
});

const cronologiaSchema = z.object({
	evento: z.string().min(2, 'Descreva o evento'),
	data: z.string().min(1, 'Informe a data'),
	hora: z.string().min(1, 'Informe a hora'),
	setor: z.string().min(1, 'Informe o setor'),
	descricao: z.string().min(5, 'Descrição muito curta'),
});

const planoSchema = z.object({
	causas_prioritarias: z.string().min(3, 'Informe as causas prioritárias'),
	acoes_planejadas: z.string().min(3, 'Informe as ações planejadas'),
	responsavel: z.string().min(1, 'Informe o responsável'),
	prazo: z
		.any()
		.superRefine((value, ctx) => {
			if (value === '' || value === null || value === undefined) {
				ctx.addIssue({
					code: z.ZodIssueCode.custom,
					message: 'Informe o prazo',
				});
				return;
			}

			const rawValue = String(value).trim();

			if (!rawValue) {
				ctx.addIssue({
					code: z.ZodIssueCode.custom,
					message: 'Informe o prazo',
				});
				return;
			}

			if (!/^\d+$/.test(rawValue)) {
				ctx.addIssue({
					code: z.ZodIssueCode.custom,
					message: 'Informe apenas números inteiros',
				});
				return;
			}

			const prazoNumero = Number(rawValue);

			if (Number.isNaN(prazoNumero) || !Number.isInteger(prazoNumero)) {
				ctx.addIssue({
					code: z.ZodIssueCode.custom,
					message: 'Informe apenas números',
				});
				return;
			}

			if (prazoNumero <= 0) {
				ctx.addIssue({
					code: z.ZodIssueCode.custom,
					message: 'Informe o prazo',
				});
				return;
			}

			if (prazoNumero >= 300) {
				ctx.addIssue({
					code: z.ZodIssueCode.custom,
					message: 'O prazo máximo deve ser menor que 300 dias',
				});
			}
		})
		.transform((value) => Number(String(value).trim())),
	status: z
		.union([
			z.literal(''),
			z.enum(['PENDENTE', 'EM_ANDAMENTO', 'CONCLUIDO', 'CANCELADO']),
		])
		.refine((v) => v !== '', {
			message: 'Selecione um status',
		}),
});

const causasSchema = z
	.object({
		fator_paciente: z.string().optional(),
		fator_profissional: z.string().optional(),
		fator_tarefa: z.string().optional(),
		fator_comunicacao: z.string().optional(),
		fator_equipe: z.string().optional(),
		fator_equipamento: z.string().optional(),
		fator_ambiente: z.string().optional(),
		fator_organizacional: z.string().optional(),
		fator_treinamento: z.string().optional(),
	})
	.refine(
		(val) =>
			Object.values(val).some(
				(v) => typeof v === 'string' && v.trim().length > 0,
			),
		{
			message:
				'Preencha pelo menos um fator que contribuiu para o incidente',
			path: ['fator_paciente'], // ancora em um campo
		},
	);

const investigacaoSchema = z.object({
	coleta_dados: z
		.string()
		.min(10, 'Descreva a coleta de dados (mínimo 10 caracteres)'),
	equipe: z.array(equipeSchema).min(1, 'Adicione pelo menos um investigador'),
	cronologia: z
		.array(cronologiaSchema)
		.min(1, 'Adicione pelo menos um evento'),
	planos: z.array(planoSchema).min(1, 'Adicione pelo menos um plano de ação'),
	causas: causasSchema,
});

const investigationLookupInFlight = new Map();
const savedInvestigationBlockInFlight = new Map();

export const CAUSAS_FORM_TO_API_FIELDS = {
	fator_paciente: 'fatores_paciente',
	fator_profissional: 'fatores_profissionais',
	fator_tarefa: 'fatores_tarefa',
	fator_comunicacao: 'fatores_comunicacao',
	fator_equipe: 'fatores_equipe',
	fator_ambiente: 'fatores_ambiente',
	fator_organizacional: 'fatores_organizacionais',
	fator_treinamento: 'fatores_treinamento',
	fator_equipamento: 'fatores_equipamento',
};

const normalizeCausasValue = (value) =>
	typeof value === 'string' ? value.trim() : '';

export const getCausasPayload = (causas = {}, investigacao) => {
	const source = causas && typeof causas === 'object' ? causas : {};
	const hasContent = Object.keys(CAUSAS_FORM_TO_API_FIELDS).some((field) =>
		normalizeCausasValue(source[field]),
	);

	if (!hasContent) return null;

	return Object.entries(CAUSAS_FORM_TO_API_FIELDS).reduce(
		(payload, [formField, apiField]) => ({
			...payload,
			[apiField]: normalizeCausasValue(source[formField]),
		}),
		{ investigacao },
	);
};

const getRequestCacheKey = (...parts) =>
	parts.map((part) => String(part || '').trim()).join('|');

const requestInvByProtocolOnce = (token, protocolo) => {
	const key = getRequestCacheKey('investigacao', token, protocolo);
	if (investigationLookupInFlight.has(key)) {
		return investigationLookupInFlight.get(key);
	}

	const requestPromise = requestInvByProtocol(token, protocolo).finally(() => {
		investigationLookupInFlight.delete(key);
	});
	investigationLookupInFlight.set(key, requestPromise);
	return requestPromise;
};

const requestSavedDataOnce = (token, invId, type) => {
	const key = getRequestCacheKey('investigacao-bloco', token, invId, type);
	if (savedInvestigationBlockInFlight.has(key)) {
		return savedInvestigationBlockInFlight.get(key);
	}

	const requestPromise = requestSavedData(token, invId, type).finally(() => {
		savedInvestigationBlockInFlight.delete(key);
	});
	savedInvestigationBlockInFlight.set(key, requestPromise);
	return requestPromise;
};

const getInvestigationIdFromPayload = (payload) => {
	if (Array.isArray(payload)) {
		return payload[0]?.id || null;
	}

	return (
		payload?.id ||
		payload?.investigacao?.id ||
		payload?.data?.id ||
		payload?.resultado?.id ||
		null
	);
};

// --- Componente -------------------------------------------------------------

export default function NewInvestigacao({ notificacao, cards }) {
	const protocolo = notificacao?.protocolo;
	const [saveError, setSaveError] = useState('');
	const [submitError, setSubmitError] = useState('');
	const [saving, setSaving] = useState(false);
	const [sending, setSending] = useState(false);
	const [enviado, setEnviado] = useState(false);
	const [pdfBaixado] = useState(false);

	const notificacaoRef = useRef(null);
	const classificacaoRef = useRef(null);
	const loadedInvestigationKeyRef = useRef('');

	const {
		control,
		register,
		handleSubmit,
		getValues,
		setValue,
		trigger,
		watch,
		reset,
		formState: { errors },
	} = useForm({
		resolver: zodResolver(investigacaoSchema),
		mode: 'onChange',
		reValidateMode: 'onChange',
		defaultValues: {
			coleta_dados: '',
			equipe: [
				{
					nome_completo: '',
					cargo: '',
					setor: '',
					email: '',
				},
			],
			cronologia: [
				{
					evento: '',
					data: '',
					hora: '',
					setor: '',
					descricao: '',
				},
			],
			planos: [
				{
					causas_prioritarias: '',
					acoes_planejadas: '',
					responsavel: '',
					prazo: '',
					status: '',
				},
			],
			causas: {
				fator_paciente: '',
				fator_profissional: '',
				fator_tarefa: '',
				fator_comunicacao: '',
				fator_equipe: '',
				fator_equipamento: '',
				fator_ambiente: '',
				fator_organizacional: '',
				fator_treinamento: '',
			},
		},
	});

	const equipeFA = useFieldArray({ control, name: 'equipe' });
	const cronologiaFA = useFieldArray({ control, name: 'cronologia' });
	const planosFA = useFieldArray({ control, name: 'planos' });
	const getIntegerOnlyFieldProps = (fieldName) => {
		const registeredField = register(fieldName);

		return {
			...registeredField,
			inputMode: 'numeric',
			pattern: '[0-9]*',
			onBeforeInput: (event) => {
				const nextData = event?.nativeEvent?.data || '';
				if (nextData && /\D/.test(nextData)) {
					event.preventDefault();
				}
			},
			onKeyDown: (event) => {
				if (['e', 'E', '+', '-', '.', ','].includes(event.key)) {
					event.preventDefault();
				}
			},
			onPaste: (event) => {
				const pastedText = event.clipboardData.getData('text');
				if (/^\d+$/.test(pastedText)) return;

				event.preventDefault();
				const onlyDigits = pastedText.replace(/\D/g, '');
				setValue(fieldName, onlyDigits, {
					shouldDirty: true,
					shouldTouch: true,
					shouldValidate: true,
				});
			},
			onDrop: (event) => {
				event.preventDefault();
			},
			onChange: (event) => {
				const onlyDigits = event.target.value.replace(/\D/g, '');
				if (event.target.value !== onlyDigits) {
					event.target.value = onlyDigits;
				}
				registeredField.onChange(event);
			},
		};
	};
	const watchedFormValues = watch();
	const validationResult = useMemo(
		() => investigacaoSchema.safeParse(watchedFormValues || getValues()),
		[watchedFormValues, getValues],
	);
	const isFormularioPronto = validationResult.success;
	const bloqueiosFormulario = useMemo(
		() =>
			validationResult.success
				? []
				: validationResult.error.issues.map((issue) => ({
						campo: issue.path.join('.') || 'formulário',
						mensagem: issue.message,
					})),
		[validationResult],
	);

	useEffect(() => {
		/* console.log('[NewInvestigacao] status do formulário', {
			isFormularioPronto,
			bloqueiosFormulario,
			values: watchedFormValues,
		}); */
	}, [isFormularioPronto, bloqueiosFormulario, watchedFormValues]);

	const getInvestigationId = useCallback(
		async (token, allowCreate = false) => {
			if (!protocolo) return { id: null };

			const invByProtocol = await requestInvByProtocolOnce(
				token,
				protocolo,
			);

			if (invByProtocol?.error) {
				return { error: invByProtocol.error };
			}

			const existingInvId = getInvestigationIdFromPayload(invByProtocol);
			if (existingInvId) {
				return { id: existingInvId };
			}

			if (!allowCreate) {
				return { id: null };
			}

			const createResult = await requestNewInvCreation(
				token,
				protocolo,
			);

			if (createResult?.error) {
				return { error: createResult.error };
			}

			const createdInvId = getInvestigationIdFromPayload(createResult);
			if (createdInvId) {
				return { id: createdInvId };
			}

			const createdInvByProtocol = await requestInvByProtocol(
				token,
				protocolo,
			);

			if (createdInvByProtocol?.error) {
				return { error: createdInvByProtocol.error };
			}

			const fallbackCreatedInvId =
				getInvestigationIdFromPayload(createdInvByProtocol);
			if (fallbackCreatedInvId) {
				return { id: fallbackCreatedInvId };
			}

			return { id: null };
		},
		[protocolo],
	);

	// mesma normalização usada em ClassInvForm
	const normalizeField = (value) => {
		if (value === null || value === undefined) return 'NÃO PREENCHIDO';
		if (typeof value === 'string' && value.trim() === '')
			return 'NÃO PREENCHIDO';
		if (typeof value === 'number' && Number.isNaN(value)) return 0;
		return value;
	};

	// normaliza valores "vazios" vindos da API para preencher os inputs
	const normalizeEmptyValue = (value) => {
		if (
			value === 'NÃO PREENCHIDO' ||
			value === '00:00:00' ||
			value === '2000-01-01' ||
			value === null ||
			value === undefined
		) {
			return '';
		}
		return value;
	};

	useEffect(() => {
		const loadSavedInvestigation = async () => {
			try {
				if (!protocolo) return;

				const userCookie = Cookies.get('user');
				if (!userCookie) return;

				const { token } = JSON.parse(userCookie);
				if (!token) return;

				const loadKey = `${token}|${protocolo}`;
				if (loadedInvestigationKeyRef.current === loadKey) return;
				loadedInvestigationKeyRef.current = loadKey;

				// localiza a investigação pelo protocolo
				const investigationResult = await getInvestigationId(
					token,
					false,
				);

				if (investigationResult?.error || !investigationResult?.id) {
					return;
				}

				const invId = investigationResult.id;
				if (!invId) return;

				// busca dados salvos de cada bloco
				const [
					equipeSaved,
					planosSaved,
					cronologiaSaved,
					causasSaved,
					coletaSaved,
				] = await Promise.all([
					requestSavedDataOnce(token, invId, 'equipe'),
					requestSavedDataOnce(token, invId, 'planos-acao'),
					requestSavedDataOnce(token, invId, 'cronologia'),
					requestSavedDataOnce(token, invId, 'causas'),
					requestSavedDataOnce(token, invId, 'coleta-dados'),
				]);

				const currentValues = getValues();
				const latestCausas = getLatestSavedRecord(causasSaved);

				const newValues = {
					...currentValues,
					coleta_dados:
						Array.isArray(coletaSaved) && coletaSaved.length > 0
							? normalizeEmptyValue(
									coletaSaved[0]?.dados_coletados,
								)
							: currentValues.coleta_dados,
					equipe:
						Array.isArray(equipeSaved) && equipeSaved.length > 0
							? equipeSaved.map((membro) => ({
									nome_completo: normalizeEmptyValue(
										membro.nome_completo,
									),
									cargo: normalizeEmptyValue(membro.cargo),
									setor: normalizeEmptyValue(membro.setor),
									email: normalizeEmptyValue(membro.email),
								}))
							: currentValues.equipe,
					planos:
						Array.isArray(planosSaved) && planosSaved.length > 0
							? planosSaved.map((plano) => ({
									causas_prioritarias: normalizeEmptyValue(
										plano.causas_prioritarias,
									),
									acoes_planejadas: normalizeEmptyValue(
										plano.acoes_planejadas,
									),
									responsavel: normalizeEmptyValue(
										plano.responsabilidade,
									),
									prazo: normalizeEmptyValue(
										plano.prazo === 32767
											? ''
											: plano.prazo,
									),
									status:
										normalizeEmptyValue(plano.status) || '',
								}))
							: currentValues.planos,
					cronologia:
						Array.isArray(cronologiaSaved) &&
						cronologiaSaved.length > 0
							? [...cronologiaSaved]
									.sort((a, b) => a.id - b.id)
									.map((cron) => ({
										evento: normalizeEmptyValue(
											cron.evento,
										),
										data: normalizeEmptyValue(
											cron.data_evento,
										),
										hora: normalizeEmptyValue(
											cron.hora_evento,
										),
										setor: normalizeEmptyValue(cron.local),
										descricao: normalizeEmptyValue(
											cron.descricao,
										),
									}))
							: currentValues.cronologia,
					causas: latestCausas
						? {
								fator_paciente: normalizeEmptyValue(
									latestCausas.fatores_paciente,
								),
								fator_profissional: normalizeEmptyValue(
									latestCausas.fatores_profissionais,
								),
								fator_tarefa: normalizeEmptyValue(
									latestCausas.fatores_tarefa,
								),
								fator_comunicacao: normalizeEmptyValue(
									latestCausas.fatores_comunicacao,
								),
								fator_equipe: normalizeEmptyValue(
									latestCausas.fatores_equipe,
								),
								fator_equipamento: normalizeEmptyValue(
									latestCausas.fatores_equipamento,
								),
								fator_ambiente: normalizeEmptyValue(
									latestCausas.fatores_ambiente,
								),
								fator_organizacional: normalizeEmptyValue(
									latestCausas.fatores_organizacionais,
								),
								fator_treinamento: normalizeEmptyValue(
									latestCausas.fatores_treinamento,
								),
							}
						: currentValues.causas,
				};

				reset(newValues);
				await trigger();
			} catch (error) {
				console.error('Erro ao carregar investigação salva:', error);
			}
		};

		loadSavedInvestigation();
	}, [protocolo, getValues, reset, trigger, getInvestigationId]);

	const onInvalid = () => {
		const msg =
			'Existem campos obrigatórios não preenchidos ou inválidos. Verifique os erros destacados.';
		// para envio final apenas
		setSubmitError(msg);
	};

	const handleSave = async (data) => {
		setSaveError('');
		setSubmitError('');
		setSaving(true);
		try {
			const userCookie = Cookies.get('user');
			if (!userCookie) {
				setSaveError('Usuário não autenticado. Faça login novamente.');
				return;
			}

			const { token } = JSON.parse(userCookie);
			if (!token) {
				setSaveError('Token de autenticação inválido.');
				return;
			}

			// busca investigação existente para este protocolo
			const investigationResult = await getInvestigationId(token, true);

			let invId;
			if (investigationResult?.error) {
				setSaveError(
					'Não foi possível localizar a investigação para este protocolo.',
				);
				return;
			}

			if (investigationResult?.id) {
				invId = investigationResult.id;
			} else {
				setSaveError(
					'Nenhuma investigação encontrada para este protocolo.',
				);
				return;
			}

			// monta payloads por bloco, compatíveis com a API atual
			const equipePayload = data.equipe.map((item) => ({
				investigacao: invId,
				nome_completo: normalizeField(item.nome_completo),
				setor: normalizeField(item.setor),
				cargo: normalizeField(item.cargo),
				email:
					normalizeField(item.email) === 'NÃO PREENCHIDO'
						? 'emptyemail@gmail.com'
						: normalizeField(item.email),
			}));

			const planosPayload = data.planos.map((item) => ({
				investigacao: invId,
				causas_prioritarias: normalizeField(item.causas_prioritarias),
				acoes_planejadas: normalizeField(item.acoes_planejadas),
				responsabilidade: normalizeField(item.responsavel),
				prazo:
					typeof item.prazo === 'string' && item.prazo.trim() === ''
						? 32767
						: normalizeField(item.prazo),
				status:
					normalizeField(item.status) === 'NÃO PREENCHIDO'
						? 'PENDENTE'
						: item.status?.toUpperCase() || '',
			}));

			const cronologiaPayload = data.cronologia.map((item) => ({
				investigacao: invId,
				evento: normalizeField(item.evento),
				data_evento:
					!item.data || item.data.trim() === ''
						? '2000-01-01'
						: normalizeField(item.data),
				hora_evento:
					!item.hora || item.hora.trim() === ''
						? '00:00:00'
						: normalizeField(item.hora),
				local: normalizeField(item.setor),
				descricao: normalizeField(item.descricao),
			}));

			const causasPayload = getCausasPayload(data.causas, invId);

			const blocoErrors = [];

			await requestSaveForm(token, invId, 'equipe', equipePayload)
				.then((res) => {
					if (res?.error) {
						blocoErrors.push('Equipe de investigação');
					}
				})
				.catch((err) => {
					console.error(err);
					blocoErrors.push('Equipe de investigação');
				});

			await requestSaveForm(token, invId, 'planos-acao', planosPayload)
				.then((res) => {
					if (res?.error) {
						blocoErrors.push('Plano de ação');
					}
				})
				.catch((err) => {
					console.error(err);
					blocoErrors.push('Plano de ação');
				});

			await requestSaveForm(token, invId, 'coleta-dados', {
				investigacao: invId,
				dados_coletados: normalizeField(data.coleta_dados),
			})
				.then((res) => {
					if (res?.error) {
						blocoErrors.push('Coleta de dados');
					}
				})
				.catch((err) => {
					console.error(err);
					blocoErrors.push('Coleta de dados');
				});

			await requestSaveForm(token, invId, 'cronologia', cronologiaPayload)
				.then((res) => {
					if (res?.error) {
						blocoErrors.push('Cronologia do incidente');
					}
				})
				.catch((err) => {
					console.error(err);
					blocoErrors.push('Cronologia do incidente');
				});

			if (causasPayload) {
				await requestSaveForm(token, invId, 'causas', causasPayload)
					.then((res) => {
						if (res?.error) {
							blocoErrors.push('Definição das causas');
						}
					})
					.catch((err) => {
						console.error(err);
						blocoErrors.push('Definição das causas');
					});
			}

			if (blocoErrors.length > 0) {
				setSaveError(
					`Alguns blocos não puderam ser salvos: ${blocoErrors.join(
						', ',
					)}. Revise os dados e tente novamente.`,
				);
			}
		} catch (e) {
			console.error(e);
			setSaveError(
				'Não foi possível salvar a investigação. Tente novamente mais tarde.',
			);
		} finally {
			setSaving(false);
		}
	};

	const handleSend = async (data) => {
		setSubmitError('');
		setSaveError('');
		setSending(true);
		try {
			const userCookie = Cookies.get('user');
			if (!userCookie) {
				setSubmitError(
					'Usuário não autenticado. Faça login novamente.',
				);
				return;
			}

			const { token } = JSON.parse(userCookie);
			if (!token) {
				setSubmitError('Token de autenticação inválido.');
				return;
			}

			// busca investigação existente para este protocolo
			const investigationResult = await getInvestigationId(token, true);

			let invId;
			if (investigationResult?.error) {
				setSubmitError(
					'Não foi possível localizar a investigação para este protocolo.',
				);
				return;
			}

			if (investigationResult?.id) {
				invId = investigationResult.id;
			} else {
				setSubmitError(
					'Nenhuma investigação encontrada para este protocolo.',
				);
				return;
			}

			// monta payloads por bloco, compatíveis com a API atual
			const equipePayload = data.equipe.map((item) => ({
				investigacao: invId,
				nome_completo: normalizeField(item.nome_completo),
				setor: normalizeField(item.setor),
				cargo: normalizeField(item.cargo),
				email:
					normalizeField(item.email) === 'NÃO PREENCHIDO'
						? 'emptyemail@gmail.com'
						: normalizeField(item.email),
			}));

			const planosPayload = data.planos.map((item) => ({
				investigacao: invId,
				causas_prioritarias: normalizeField(item.causas_prioritarias),
				acoes_planejadas: normalizeField(item.acoes_planejadas),
				responsabilidade: normalizeField(item.responsavel),
				prazo:
					typeof item.prazo === 'string' && item.prazo.trim() === ''
						? 32767
						: normalizeField(item.prazo),
				status:
					normalizeField(item.status) === 'NÃO PREENCHIDO'
						? 'PENDENTE'
						: item.status?.toUpperCase() || '',
			}));

			const cronologiaPayload = data.cronologia.map((item) => ({
				investigacao: invId,
				evento: normalizeField(item.evento),
				data_evento:
					!item.data || item.data.trim() === ''
						? '2000-01-01'
						: normalizeField(item.data),
				hora_evento:
					!item.hora || item.hora.trim() === ''
						? '00:00:00'
						: normalizeField(item.hora),
				local: normalizeField(item.setor),
				descricao: normalizeField(item.descricao),
			}));

			const causasPayload = getCausasPayload(data.causas, invId);

			// salva cada parte da investigação; se qualquer parte falhar,
			// interrompe o fluxo e não avança para o FinishCard
			const equipeRes = await requestSaveForm(
				token,
				invId,
				'equipe',
				equipePayload,
			);
			if (equipeRes?.error) {
				console.error(equipeRes.error);
				setSubmitError(
					'Não foi possível salvar a equipe de investigação. Tente novamente.',
				);
				return;
			}

			const planosRes = await requestSaveForm(
				token,
				invId,
				'planos-acao',
				planosPayload,
			);
			if (planosRes?.error) {
				console.error(planosRes.error);
				setSubmitError(
					'Não foi possível salvar o plano de ação. Tente novamente.',
				);
				return;
			}

			const coletaRes = await requestSaveForm(
				token,
				invId,
				'coleta-dados',
				{
					investigacao: invId,
					dados_coletados: normalizeField(data.coleta_dados),
				},
			);
			if (coletaRes?.error) {
				console.error(coletaRes.error);
				setSubmitError(
					'Não foi possível salvar a coleta de dados. Tente novamente.',
				);
				return;
			}

			const cronologiaRes = await requestSaveForm(
				token,
				invId,
				'cronologia',
				cronologiaPayload,
			);
			if (cronologiaRes?.error) {
				console.error(cronologiaRes.error);
				setSubmitError(
					'Não foi possível salvar a cronologia do incidente. Tente novamente.',
				);
				return;
			}

			if (causasPayload) {
				const causasRes = await requestSaveForm(
					token,
					invId,
					'causas',
					causasPayload,
				);
				if (causasRes?.error) {
					console.error(causasRes.error);
					setSubmitError(
						'Não foi possível salvar a definição das causas. Tente novamente.',
					);
					return;
				}
			}

			// conclui a investigação (envio final), como no ClassInvForm
			const concludeResult = await requestInvConclude(token, invId, {
				id: invId,
			});

			if (concludeResult?.error) {
				console.error(concludeResult.error);
				setSubmitError(
					'Não foi possível enviar a investigação. Tente novamente mais tarde.',
				);
				return;
			}

			setEnviado(true);
		} catch (e) {
			console.error(e);
			setSubmitError(
				'Não foi possível enviar a investigação. Verifique os dados e tente novamente.',
			);
		} finally {
			setSending(false);
		}
	};

	if (enviado) {
		return (
			<FinishCard
				stage='investigacao'
				requestResult={{
					notificacao,
					investigacao: getValues(),
					// para reutilizar o mesmo FinishCard da classificação,
					// passamos um objeto mínimo de classificacao; o PDF tratará
					// o prazo em branco como "Não carregado".
					classificacao: { prazo_investigacao: '' },
				}}
			/>
		);
	}

	return (
		<div className='w-[95%] max-w-[1136px] font-inter mx-auto mt-[80px] flex flex-col gap-10'>
			{/* <h1 className='text-2xl font-semibold text-[#004A80] mb-2'>
				Investigação do incidente
			</h1>
			<p className='text-sm text-gray-500 mb-4'>
				Protocolo:{' '}
				<span className='font-semibold'>
					{notificacao?.protocolo || '—'}
				</span>
			</p> */}

			<div className='bg-[#F0F4F8] flex rounded-sm w-full content-center min-h-[142px] p-[32px] gap-[40px] justify-between lg:flex-row flex-col'>
				<div className='content-center items-center mx-auto'>
					<img
						className=' w-[200px]  sm:w-[300px] h-auto'
						src={logo}
						alt='logo'></img>
				</div>
				<p className='text-[28px] sm:text-[32px] font-bold text-[#13335A] content-center  max-w-[714px] text-center'>
					NOTIFICAÇÃO DE INCIDENTES E NÃO CONFORMIDADES
				</p>
			</div>
			{/* FORMULARIO */}
			<NewNotificacaoPreenchido notificacao={notificacao} />
			<NewClassificacaoPreenchido notificacao={notificacao} />
			{cards.caso !== 'CLASSIFICADO' && (
				<div className='w-full flex justify-start'>
					<button
						className='enabled:bg-[#E3E3E3] max-h-[40px] text-[#a3a3a3] enabled:text-[#757575] flex bg-[#d3d3d3]  py-2 px-2 rounded-md enabled:hover:bg-[#757575] enabled:hover:text-[#f0f0f0] transition-all duration-200 focus:outline-none font-semibold mt-5'
						onClick={() => {
							generatePDF(
								[notificacaoRef, classificacaoRef],
								'notificacao.pdf',
							);
						}}>
						{pdfBaixado ? (
							'Download Concluído'
						) : (
							<>
								<FaRegFilePdf className=' w-[40px] h-auto text-[10px]' />{' '}
								PDF
							</>
						)}
					</button>
				</div>
			)}

			{(saveError || submitError) && (
				<div className='mb-4 rounded border border-red-300 bg-red-50 px-3 py-2 text-sm text-red-700'>
					{saveError || submitError}
				</div>
			)}
			<div
				style={{
					position: 'absolute',
					left: '-9999px',
					top: 0,
				}}>
				<div ref={notificacaoRef}>
					<NewNotificacaoPDF notificacao={notificacao} />
				</div>

				<div ref={classificacaoRef}>
					<NewClassificacaoPDF notificacao={notificacao} />
				</div>
			</div>
			{cards.caso === 'CLASSIFICADO' && (
				<form className='flex flex-col gap-6'>
					{/* EQUIPE DE INVESTIGAÇÃO */}
					<section className='border border-[#E3E3E3] rounded-md '>
						<h2 className='text-lg font-semibold border-b border-[#E3E3E] rounded-t-lg text-center bg-[#F4F4F4] text-[#004A80] p-1'>
							EQUIPE DE INVESTIGAÇÃO
						</h2>
						<div className='p-3'>
							{errors.equipe && (
								<p className='text-red-500 text-xs mb-2'>
									{errors.equipe.message}
								</p>
							)}
							<div className='flex flex-col gap-1'>
								{equipeFA.fields.map((field, index) => (
									<div
										key={field.id}
										className='flex flex-wrap gap-3 md:gap-4 items-start   rounded-md px-2 py-2'>
										<div className='flex flex-col flex-1 min-w-[200px]'>
											<label className='text-[15px] text-[#004A80] font-medium'>
												Nome
											</label>
											<input
												{...register(
													`equipe.${index}.nome_completo`,
												)}
												className={inputBaseClass}
												placeholder='Nome completo'
											/>
											{errors.equipe?.[index]
												?.nome_completo && (
												<p className='text-red-500 text-xs'>
													{
														errors.equipe[index]
															.nome_completo
															.message
													}
												</p>
											)}
										</div>
										<div className='flex flex-col flex-1 min-w-[160px]'>
											<label className='text-[15px] text-[#004A80] font-medium'>
												Cargo
											</label>
											<input
												{...register(
													`equipe.${index}.cargo`,
												)}
												className={inputBaseClass}
												placeholder='Cargo'
											/>
											{errors.equipe?.[index]?.cargo && (
												<p className='text-red-500 text-xs'>
													{
														errors.equipe[index]
															.cargo.message
													}
												</p>
											)}
										</div>
										<div className='flex flex-col flex-1 min-w-[160px]'>
											<label className='text-[15px] text-[#004A80] font-medium'>
												Setor
											</label>
											<input
												{...register(
													`equipe.${index}.setor`,
												)}
												className={inputBaseClass}
												placeholder='Setor'
											/>
											{errors.equipe?.[index]?.setor && (
												<p className='text-red-500 text-xs'>
													{
														errors.equipe[index]
															.setor.message
													}
												</p>
											)}
										</div>
										<div className='flex flex-col flex-1 min-w-[200px]'>
											<label className='text-[15px] text-[#004A80] font-medium'>
												Email
											</label>
											<input
												{...register(
													`equipe.${index}.email`,
												)}
												className={inputBaseClass}
												placeholder='email@exemplo.com'
											/>
											{errors.equipe?.[index]?.email && (
												<p className='text-red-500 text-xs'>
													{
														errors.equipe[index]
															.email.message
													}
												</p>
											)}
										</div>

										{equipeFA.fields.length > 1 && (
											<div className='flex flex-col justify-center mt-[23px]'>
												<button
													type='button'
													onClick={() =>
														equipeFA.remove(index)
													}
													className='bg-white border p-2 border-[#E3E3E3] rounded-md text-[#B74644] hover:bg-[#B74644] hover:border-[#B74644]  hover:text-white text-2xl transition-all duration-300 leading-none min-h-[40px]'>
													<LuMinus className='w-[18px] h-auto' />
												</button>
											</div>
										)}
									</div>
								))}
							</div>
							<div className='flex items-center w-full justify-start p-2'>
								<button
									type='button'
									onClick={() =>
										equipeFA.append({
											nome_completo: '',
											cargo: '',
											setor: '',
											email: '',
										})
									}
									className='flex items-center justify-center gap-1 text-xs border border-[#E3E3E3] rounded-lg px-2 py-1 text-[#004A80] hover:bg-[#004A80] hover:text-white transition min-h-[40px]'>
									<GoPlus className='w-[20px] h-auto' />{' '}
									Adicionar membro
								</button>
							</div>
						</div>
					</section>
					{/* COLETA DE DADOS */}

					<section className='border border-[#E3E3E3] rounded-md '>
						<div className='text-lg font-semibold border-b border-[#E3E3E] rounded-t-lg text-center bg-[#F4F4F4] text-[#004A80] p-1'>
							<div>
								<h2>COLETA E ORGANIZAÇÃO DE DADOS</h2>
								<p className='text-[16px] font-normal text-[#004A80]'>
									Utilizar metodologia apropriada:
									entrevistas, consulta e prontuários ou
									documentos da instituição, busca ativa em
									campo, entre outras.
								</p>
							</div>
						</div>
						<div className='p-3'>
							<textarea
								{...register('coleta_dados')}
								className={`${textareaBaseClass} min-h-[150px]`}
								placeholder='Descreva como foi feita a coleta de dados (entrevistas, prontuários, observação em campo, etc.)'
							/>
							{errors.coleta_dados && (
								<p className='text-red-500 text-xs mt-1'>
									{errors.coleta_dados.message}
								</p>
							)}
						</div>
					</section>

					{/* CRONOLOGIA DO INCIDENTE */}
					<section className='border border-[#E3E3E3] rounded-md '>
						<div className='text-lg font-semibold border-b border-[#E3E3E] rounded-t-lg text-center bg-[#F4F4F4] text-[#004A80] p-1'>
							<div>
								<h2>CRONOLOGIA DO INCIDENTE</h2>
							</div>
						</div>
						<div className='p-3'>
							{errors.cronologia && (
								<p className='text-red-500 text-xs mb-2'>
									{errors.cronologia.message}
								</p>
							)}
							<div className='flex flex-col gap-1'>
								{cronologiaFA.fields.map((field, index) => {
									const dataValue = watch(
										`cronologia.${index}.data`,
									);
									const horaValue = watch(
										`cronologia.${index}.hora`,
									);

									return (
										<div
											key={field.id}
											className='rounded-md p-2'>
											<div className='flex flex-wrap gap-3 md:gap-4 items-start'>
												<div className='flex flex-col flex-1 min-w-[200px]'>
													<label className='text-[15px] text-[#004A80] font-medium'>
														Evento
													</label>
													<input
														{...register(
															`cronologia.${index}.evento`,
														)}
														className={
															inputBaseClass
														}
														placeholder='Descrição breve do evento'
													/>
													{errors.cronologia?.[index]
														?.evento && (
														<p className='text-red-500 text-xs'>
															{
																errors
																	.cronologia[
																	index
																].evento.message
															}
														</p>
													)}
												</div>
												<div className='flex flex-col min-w-[140px]'>
													<label className='text-[15px] text-[#004A80] font-medium'>
														Data
													</label>
													<input
														type='date'
														{...register(
															`cronologia.${index}.data`,
														)}
														className={`${inputBaseClass} ${
															!dataValue
																? 'text-gray-400'
																: 'text-gray-900'
														}`}
													/>
													{errors.cronologia?.[index]
														?.data && (
														<p className='text-red-500 text-xs'>
															{
																errors
																	.cronologia[
																	index
																].data.message
															}
														</p>
													)}
												</div>
												<div className='flex flex-col min-w-[120px]'>
													<label className='text-[15px] text-[#004A80] font-medium'>
														Hora
													</label>
													<input
														type='time'
														{...register(
															`cronologia.${index}.hora`,
														)}
														className={`${inputBaseClass} ${
															!horaValue
																? 'text-gray-400'
																: 'text-gray-900'
														}`}
													/>
													{errors.cronologia?.[index]
														?.hora && (
														<p className='text-red-500 text-xs'>
															{
																errors
																	.cronologia[
																	index
																].hora.message
															}
														</p>
													)}
												</div>
												<div className='flex flex-col flex-1 min-w-[200px]'>
													<label className='text-[15px] text-[#004A80] font-medium'>
														Local / Setor
													</label>
													<input
														{...register(
															`cronologia.${index}.setor`,
														)}
														className={
															inputBaseClass
														}
														placeholder='Setor onde ocorreu o evento'
													/>
													{errors.cronologia?.[index]
														?.setor && (
														<p className='text-red-500 text-xs'>
															{
																errors
																	.cronologia[
																	index
																].setor.message
															}
														</p>
													)}
												</div>
												<div className='flex flex-col flex-[2] min-w-[220px]'>
													<label className='text-[15px] text-[#004A80] font-medium'>
														Descrição
													</label>
													<textarea
														{...register(
															`cronologia.${index}.descricao`,
														)}
														className={
															textareaBaseClass
														}
														placeholder='Descreva detalhadamente o que aconteceu'
													/>
													{errors.cronologia?.[index]
														?.descricao && (
														<p className='text-red-500 text-xs'>
															{
																errors
																	.cronologia[
																	index
																].descricao
																	.message
															}
														</p>
													)}
												</div>
												{cronologiaFA.fields.length >
													1 && (
													<div className='flex flex-col justify-start mt-[21px]'>
														<button
															type='button'
															onClick={() =>
																cronologiaFA.remove(
																	index,
																)
															}
															className='bg-white border p-2 border-[#E3E3E3] rounded-md text-[#B74644] hover:bg-[#B74644] hover:border-[#B74644] hover:text-white text-2xl transition-all duration-300 leading-none min-h-[40px]'>
															<LuMinus className='w-[18px] h-auto' />
														</button>
													</div>
												)}
											</div>
										</div>
									);
								})}
							</div>
							<div className='flex items-center w-full justify-start p-2'>
								<button
									type='button'
									onClick={() =>
										cronologiaFA.append({
											evento: '',
											data: '',
											hora: '',
											setor: '',
											descricao: '',
										})
									}
									className='flex items-center justify-center gap-1 text-xs border border-[#E3E3E3] rounded-lg px-3 py-2 text-[#004A80] hover:bg-[#004A80] hover:text-white transition mb-3 min-h-[40px]'>
									<GoPlus className='w-[18px] h-auto' />{' '}
									Adicionar evento
								</button>
							</div>
						</div>
					</section>

					{/* DEFINIÇÃO DAS CAUSAS */}

					<section className='border border-[#E3E3E3] rounded-md '>
						<div className='text-lg font-semibold border-b border-[#E3E3E] rounded-t-lg text-center bg-[#F4F4F4] text-[#004A80] p-1'>
							<div>
								<h2>
									DEFINIÇÃO DAS CAUSAS - FATORES QUE
									CONTRIBUÍRAM PARA O INCIDENTE
								</h2>
								{/* <p className='text-[16px] font-normal text-[#004A80]'>
								Preencha pelo menos um destes campos
							</p> */}
							</div>
						</div>
						<p className='px-3 pt-2 text-[14px] font-normal text-[#8f8f8f]'>
							Preencha pelo menos um destes campos
						</p>
						<div className='p-3'>
							<div className='grid grid-cols-1 md:grid-cols-2 gap-3 text-sm'>
								<div>
									<label className='text-[15px] text-[#004A80] font-medium'>
										Fatores do paciente
									</label>
									<textarea
										{...register('causas.fator_paciente')}
										className={textareaBaseClass}
										placeholder='Ex.: condição clínica, adesão ao tratamento, compreensão das orientações'
									/>
								</div>
								<div>
									<label className='text-[15px] text-[#004A80] font-medium'>
										Fatores dos profissionais
									</label>
									<textarea
										{...register(
											'causas.fator_profissional',
										)}
										className={textareaBaseClass}
										placeholder='Ex.: distração, fadiga, treinamento insuficiente'
									/>
								</div>
								<div>
									<label className='text-[15px] text-[#004A80] font-medium'>
										Fatores da tarefa / atividade
									</label>
									<textarea
										{...register('causas.fator_tarefa')}
										className={textareaBaseClass}
										placeholder='Ex.: complexidade da tarefa, protocolos, rotinas'
									/>
								</div>
								<div>
									<label className='text-[15px] text-[#004A80] font-medium'>
										Fatores de comunicação
									</label>
									<textarea
										{...register(
											'causas.fator_comunicacao',
										)}
										className={textareaBaseClass}
										placeholder='Ex.: falhas em passagens de plantão, registros incompletos'
									/>
								</div>
								<div>
									<label className='text-[15px] text-[#004A80] font-medium'>
										Fatores de equipamentos
									</label>
									<textarea
										{...register(
											'causas.fator_equipamento',
										)}
										className={textareaBaseClass}
										placeholder='Ex.: falhas, manutenção, indisponibilidade de equipamentos'
									/>
								</div>
								<div>
									<label className='text-[15px] text-[#004A80] font-medium'>
										Fatores do ambiente
									</label>
									<textarea
										{...register('causas.fator_ambiente')}
										className={textareaBaseClass}
										placeholder='Ex.: iluminação, ruído, fluxo de pessoas'
									/>
								</div>
								<div>
									<label className='text-[15px] text-[#004A80] font-medium'>
										Fatores organizacionais
									</label>
									<textarea
										{...register(
											'causas.fator_organizacional',
										)}
										className={textareaBaseClass}
										placeholder='Ex.: carga de trabalho, dimensionamento, políticas internas'
									/>
								</div>
								<div>
									<label className='text-[15px] text-[#004A80] font-medium'>
										Fatores de treinamento / educação
									</label>
									<textarea
										{...register(
											'causas.fator_treinamento',
										)}
										className={textareaBaseClass}
										placeholder='Ex.: ausência de capacitação, atualizações, educação continuada'
									/>
								</div>
							</div>
						</div>
						{errors.causas && (
							<p className='text-red-500 text-xs mt-1'>
								{errors.causas.message}
							</p>
						)}
					</section>

					{/* PLANO DE AÇÃO */}
					<section className='border border-[#E3E3E3] rounded-md '>
						<div className='text-lg font-semibold border-b border-[#E3E3E] rounded-t-lg text-center bg-[#F4F4F4] text-[#004A80] p-1'>
							<div>
								<h2>PLANO DE AÇÃO</h2>
								{/* <p className='text-[16px] font-normal text-[#004A80]'>
								Preencha pelo menos um destes campos
							</p> */}
							</div>
						</div>
						{/* <p className='px-3 pt-2 text-[14px] font-normal text-[#8f8f8f]'>
						Preencha pelo menos um destes campos
					</p> */}
						<div className='p-3'>
							{errors.planos && (
								<p className='text-red-500 text-xs mb-2'>
									{errors.planos.message}
								</p>
							)}
							<div className='flex flex-col gap-1'>
								{planosFA.fields.map((field, index) => {
									const statusValue = watch(
										`planos.${index}.status`,
									);

									return (
										<div
											key={field.id}
											className='rounded-md p-2'>
											<div className='flex flex-wrap gap-3 md:gap-4 items-start'>
												<div className='flex flex-col flex-1 min-w-[200px]'>
													<label className='text-[15px] text-[#004A80] font-medium'>
														Causas prioritárias
													</label>
													<textarea
														{...register(
															`planos.${index}.causas_prioritarias`,
														)}
														className={
															textareaBaseClass
														}
														placeholder='Causas'
													/>
													{errors.planos?.[index]
														?.causas_prioritarias && (
														<p className='text-red-500 text-xs'>
															{
																errors.planos[
																	index
																]
																	.causas_prioritarias
																	.message
															}
														</p>
													)}
												</div>
												<div className='flex flex-col flex-1 min-w-[200px]'>
													<label className='text-[15px] text-[#004A80] font-medium'>
														Ações planejadas
													</label>
													<textarea
														{...register(
															`planos.${index}.acoes_planejadas`,
														)}
														className={
															textareaBaseClass
														}
														placeholder='Ações'
													/>
													{errors.planos?.[index]
														?.acoes_planejadas && (
														<p className='text-red-500 text-xs'>
															{
																errors.planos[
																	index
																]
																	.acoes_planejadas
																	.message
															}
														</p>
													)}
												</div>

												<div className='flex flex-col min-w-[160px]'>
													<label className='text-[15px] text-[#004A80] font-medium'>
														Responsável
													</label>
													<input
														{...register(
															`planos.${index}.responsavel`,
														)}
														className={
															inputBaseClass
														}
														placeholder='Nome do responsável'
													/>
													{errors.planos?.[index]
														?.responsavel && (
														<p className='text-red-500 text-xs'>
															{
																errors.planos[
																	index
																].responsavel
																	.message
															}
														</p>
													)}
												</div>
												<div className='flex flex-col max-w-[120px]'>
													<label className='text-[15px] text-[#004A80] font-medium'>
														Prazo (dias)
													</label>
													<input
														{...getIntegerOnlyFieldProps(
															`planos.${index}.prazo`,
														)}
														className={
															inputBaseClass
														}
														placeholder='Ex.: 30'
													/>
													{errors.planos?.[index]
														?.prazo && (
														<p className='text-red-500 text-xs'>
															{
																errors.planos[
																	index
																].prazo.message
															}
														</p>
													)}
												</div>
												<div className='flex flex-col min-w-[160px]'>
													<label className='text-[15px] text-[#004A80] font-medium'>
														Status
													</label>
													<select
														{...register(
															`planos.${index}.status`,
														)}
														className={`${inputBaseClass} bg-white ${
															!statusValue
																? 'text-gray-400'
																: 'text-gray-900'
														}`}>
														<option value=''>
															Selecione o status
														</option>
														<option value='PENDENTE'>
															Pendente
														</option>
														<option value='EM_ANDAMENTO'>
															Em andamento
														</option>
														<option value='CONCLUIDO'>
															Concluído
														</option>
														<option value='CANCELADO'>
															Cancelado
														</option>
													</select>
													{errors.planos?.[index]
														?.status && (
														<p className='text-red-500 text-xs'>
															{
																errors.planos[
																	index
																].status.message
															}
														</p>
													)}
												</div>
												{planosFA.fields.length > 1 && (
													<div className='flex flex-col justify-end mt-[21px]'>
														<button
															type='button'
															onClick={() =>
																planosFA.remove(
																	index,
																)
															}
															className='bg-white border border-[#E3E3E3] rounded-md p-2 text-[#B74644] hover:bg-[#B74644] hover:border-[#B74644] hover:text-white text-2xl transition-all duration-300 leading-none min-h-[40px]'>
															<LuMinus className='w-[18px] h-auto' />
														</button>
													</div>
												)}
											</div>
										</div>
									);
								})}
							</div>
							<div className='flex items-center w-full justify-start p-2'>
								<button
									type='button'
									onClick={() =>
										planosFA.append({
											causas_prioritarias: '',
											acoes_planejadas: '',
											responsavel: '',
											prazo: '',
											status: 'PENDENTE',
										})
									}
									className='flex items-center justify-center gap-1 text-xs border border-[#E3E3E3] rounded-lg px-3 py-2 text-[#004A80] hover:bg-[#004A80] hover:text-white transition mb-3 min-h-[40px]'>
									<GoPlus className='w-[20px] h-auto' />{' '}
									Adicionar plano
								</button>
							</div>
						</div>
					</section>

					{/* BOTÕES */}
					<div className='flex flex-col sm:flex-row gap-3 justify-between mt-2'>
						<button
							type='button'
							onClick={() => handleSave(getValues())}
							disabled={saving}
							className={`w-full sm:w-[220px] border border-[#E3E3E3] rounded px-4 py-2 text-sm font-medium transition ${
								saving
									? 'bg-gray-300 text-gray-500 cursor-wait'
									: 'bg-white text-[#b6b6b6] border-[#004A80] hover:bg-[#004A80] hover:text-white'
							}`}>
							{saving ? 'Salvando...' : 'Salvar rascunho'}
						</button>

						<button
							type='button'
							onClick={handleSubmit(handleSend, onInvalid)}
							disabled={!isFormularioPronto || sending}
							className={`w-full sm:w-[220px] rounded px-4 py-2 text-sm font-medium transition ${
								!isFormularioPronto || sending
									? 'text-gray-300 border cursor-not-allowed'
									: 'bg-[#004A80] text-white hover:bg-[#193b53]'
							}`}>
							{sending
								? 'Enviando...'
								: 'Enviar para avaliação final'}
						</button>
					</div>
				</form>
			)}
		</div>
	);
}
